import { useState, useRef } from 'react';
import { CardData } from '../../types';

interface UseCardInteractionsProps {
  data: CardData;
  isConnecting: boolean;
  isCreatingPostIt: boolean;
  onCreatePostItOnCard?: (cardId: string, clientX: number, clientY: number) => void;
  onSelect: (id: string, e?: React.MouseEvent) => void;
  onDragStart: (offsetX: number, offsetY: number) => void;
  onDrag: (clientX: number, clientY: number) => void;
  onDragEnd: () => void;
  scale: number;
  pan?: { x: number; y: number };
  dragEnabled?: boolean;
}

// Flag global para evitar seleção imediata após criar post-it
let justCreatedPostIt = false;

// Exportada para que a overlay do Card também possa setar a flag
export const notifyPostItJustCreated = () => {
  justCreatedPostIt = true;
  setTimeout(() => { justCreatedPostIt = false; }, 300);
};

export const useCardInteractions = ({
  data,
  isConnecting,
  isCreatingPostIt,
  onCreatePostItOnCard,
  onSelect,
  onDragStart,
  onDrag,
  onDragEnd,
  scale,
  pan = { x: 0, y: 0 },
  dragEnabled = true,
}: UseCardInteractionsProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handlePointerDown = (e: React.PointerEvent) => {
    // Botão direito não deve selecionar nem iniciar drag; o Card/Canvas tratam o menu de contexto.
    if (e.button !== 0) return;

    // Se estamos em modo de criação de Post-it, o clique no card cria o post-it vinculado
    if (isCreatingPostIt && onCreatePostItOnCard) {
      e.stopPropagation();
      e.preventDefault();
      // Criar post-it vinculado ao card na posição do clique (usar coordenadas do pointer)
      onCreatePostItOnCard(data.id, e.clientX, e.clientY);
      // Setar flag para evitar seleção no pointerUp
      justCreatedPostIt = true;
      setTimeout(() => { justCreatedPostIt = false; }, 100);
      return;
    }

    // Bloquear apenas se for um botão
    if ((e.target as HTMLElement).closest('button')) return;
    
    // Verificar se o clique foi em um post-it (nota vinculada ao card)
    // Post-its têm o atributo data-post-it="true"
    const targetElement = e.target as HTMLElement;
    if (targetElement.closest('[data-post-it="true"]')) {
      // O clique foi em uma nota, não processar seleção do card
      return;
    }
    
    // Se estiver conectando (modo normal ou manual), não iniciar arrasto
    // Deixar o onClick processar para finalizar a conexão
    if (isConnecting) {
      // Não bloquear completamente - permitir que o onClick seja chamado
      return;
    }
    
    // Se dragEnabled for false, apenas selecionar o card (modo seleção)
    if (!dragEnabled) {
      e.stopPropagation();
      const mouseEvent = e as unknown as React.MouseEvent;
      onSelect(data.id, mouseEvent);
      return;
    }
    
    // Não bloquear o evento - permitir que o card seja arrastado
    // Apenas prevenir comportamento padrão se necessário
    e.stopPropagation(); 
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);
    startPos.current = { x: e.clientX, y: e.clientY };
    isDraggingRef.current = false;
    
    // Calcular offset usando a posição real do elemento no viewport
    const rect = target.getBoundingClientRect();
    dragOffset.current = { 
      x: e.clientX - rect.left, 
      y: e.clientY - rect.top 
    };
    
    // Converter PointerEvent para MouseEvent para compatibilidade
    const mouseEvent = e as unknown as React.MouseEvent;
    onSelect(data.id, mouseEvent);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (isConnecting || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
    if (!isDraggingRef.current && Math.hypot(e.clientX - startPos.current.x, e.clientY - startPos.current.y) > 5) {
      isDraggingRef.current = true;
      setIsDragging(true);
      onDragStart(dragOffset.current.x, dragOffset.current.y);
    }
    if (isDraggingRef.current) onDrag(e.clientX, e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (isConnecting) return;
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (!isDraggingRef.current) {
      e.stopPropagation();
      // Não selecionar card se acabamos de criar um post-it
      if (!justCreatedPostIt) {
        // Converter PointerEvent para MouseEvent para passar Ctrl/Shift
        const mouseEvent = e as unknown as React.MouseEvent;
        onSelect(data.id, mouseEvent);
      }
    } else {
      onDragEnd();
    }
    isDraggingRef.current = false;
    setIsDragging(false);
  };

  return {
    showMenu,
    setShowMenu,
    menuPos,
    setMenuPos,
    isDragging,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
};
